<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import Button from 'primevue/button'
import IftaLabel from 'primevue/iftalabel'
import SrInputNumber from '@/components/SrInputNumber.vue'
import { Form, FormField, type FormInstance, type FormSubmitEvent } from '@primevue/forms'
import { computed, ref, useTemplateRef, watch } from 'vue'
import z from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { i18n } from '@/i18n'
import { AutoTradeTriggerService } from '@/services/auto-trade-trigger-service'
import type { AutoTradeTrigger } from '@/services/auto-trade-trigger-service/types'
import { useMyToast } from '@/composable/useMyToast'

const { t } = i18n.global
const { addToast } = useMyToast()

const visible = ref(false)
const loading = ref(false)
const oneShot = ref(true)
const trigger = ref<AutoTradeTrigger | null>(null)

const settingsForm = useTemplateRef<FormInstance>('settingsForm')

const emit = defineEmits<{
  (e: 'updated'): void
}>()

const open = (data: AutoTradeTrigger) => {
  trigger.value = data
  oneShot.value = data.one_shot
  visible.value = true
}

const close = () => {
  visible.value = false
  trigger.value = null
}

defineExpose({
  open,
})

const preprocessNum = (val: unknown) => {
  if (val === '' || val === null || val === undefined) return undefined
  const n = Number(val)
  return isNaN(n) ? undefined : n
}

const schema = z.object({
  entry_spread: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v > -200 && v < 200, { message: t('modal.autoTradeTrigger.errors.entrySpreadRequired') })),
  exit_spread: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v > -200 && v < 200, { message: t('modal.autoTradeTrigger.errors.exitSpreadRequired') })),
  amount: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v >= 10 && v <= 1000, { message: t('modal.autoTradeTrigger.errors.amountRange') })),
})

const resolver = zodResolver(schema)

const initValues = computed(() => {
  if (!trigger.value) return {}
  return {
    entry_spread: trigger.value.entry_spread ?? null,
    exit_spread: trigger.value.exit_spread ?? null,
    amount: trigger.value.amount ?? null,
  }
})

watch(visible, (v) => {
  if (v && trigger.value && settingsForm.value) {
    settingsForm.value.setFieldValue('entry_spread', trigger.value.entry_spread?.toString() ?? '')
    settingsForm.value.setFieldValue('exit_spread', trigger.value.exit_spread?.toString() ?? '')
    settingsForm.value.setFieldValue('amount', trigger.value.amount?.toString() ?? '')
  }
})

const onSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid || !trigger.value) return

  const values = e.values as Record<string, unknown>
  loading.value = true
  try {
    await AutoTradeTriggerService.updateTrigger(trigger.value.id, {
      entry_spread: Number(values.entry_spread),
      exit_spread: Number(values.exit_spread),
      amount: Number(values.amount),
      one_shot: oneShot.value,
    })
    addToast({
      severity: 'success',
      summary: t('success'),
      detail: t('modal.autoTradeTrigger.updated'),
      life: 3000,
    })
    emit('updated')
    close()
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } }
      const detailData = axiosErr.response?.data?.detail
      if (Array.isArray(detailData) && detailData.length > 0) {
        const first = detailData[0] as Record<string, unknown>
        const type = typeof first.type === 'string' ? first.type : undefined
        if (type) {
          const translated = t(`modal.autoTradeTrigger.errors.${type}`, { default: null })
          if (translated !== null && translated !== `modal.autoTradeTrigger.errors.${type}`) {
            addToast({ severity: 'error', summary: t('error'), detail: translated, life: 5000 })
            return
          }
        }
        // canonical fallback matching useTrade.ts pattern
        const errorType = type ?? 'unexceptedError'
        const errorParams = (Array.isArray(first.params) ? first.params : []) as Array<string | number>
        const fallback = t(`errors.${errorType}`, errorParams)
        addToast({ severity: 'error', summary: t('error'), detail: fallback, life: 5000 })
        return
      } else if (typeof detailData === 'string') {
        addToast({ severity: 'error', summary: t('error'), detail: detailData, life: 5000 })
        return
      }
    }
    addToast({ severity: 'error', summary: t('error'), detail: t('modal.autoTradeTrigger.updateError'), life: 5000 })
  } finally {
    loading.value = false
  }
}

const handleSave = async () => {
  const result = await settingsForm.value?.validate()
  const hasErrors = result?.errors && Object.keys(result.errors).length > 0
  if (hasErrors) {
    const errors = result?.errors as Record<string, { message?: string }[]> | undefined
    const fieldLabels: Record<string, string> = {
      entry_spread: t('modal.autoTradeTrigger.entrySpread'),
      exit_spread: t('modal.autoTradeTrigger.exitSpread'),
      amount: t('usdtAmount'),
    }
    const messages = errors
      ? Object.entries(errors)
        .flatMap(([field, fieldErrors]) => {
          const label = fieldLabels[field] || field
          return fieldErrors.map((e) => `${label}: ${e.message || ''}`)
        })
        .filter((msg, idx, arr) => arr.indexOf(msg) === idx)
        .join('\n')
      : ''
    addToast({ severity: 'error', summary: t('error'), detail: messages || t('modal.autoTradeTrigger.updateError'), life: 5000 })
    return
  }
  settingsForm.value?.submit()
}
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:root="w-full lg:w-[400px]" pt:content="p-2" pt:header="px-4 py-1"
    :header="$t('modal.autoTradeTrigger.editTitle')">
    <Form ref="settingsForm" :resolver="resolver" :initialValues="initValues" @submit="onSubmit"
      class="grid grid-cols-1 gap-3">

      <FormField name="entry_spread">
        <IftaLabel>
          <SrInputNumber type="text" fluid :placeholder="$t('modal.autoTradeTrigger.entrySpread')" suffix="%"
            :min="0.01" :maxFractionDigits="2" />
          <label>{{ $t('modal.autoTradeTrigger.entrySpread') }}</label>
        </IftaLabel>
      </FormField>

      <FormField name="exit_spread">
        <IftaLabel>
          <SrInputNumber type="text" fluid :placeholder="$t('modal.autoTradeTrigger.exitSpread')" suffix="%"
            :maxFractionDigits="2" />
          <label>{{ $t('modal.autoTradeTrigger.exitSpread') }}</label>
        </IftaLabel>
      </FormField>

      <FormField name="amount">
        <IftaLabel>
          <SrInputNumber type="text" fluid :placeholder="$t('usdtAmount')" prefix="$" />
          <label>{{ $t('usdtAmount') }}</label>
        </IftaLabel>
      </FormField>

      <Button :icon="oneShot ? 'pi pi-check-square' : 'pi pi-stop'" @click="oneShot = !oneShot"
        severity="success" :variant="oneShot ? undefined : 'outlined'"
        :label="$t('modal.autoTradeTrigger.oneShot')">
      </Button>

      <div class="flex justify-end gap-2">
        <Button type="button" :loading="loading" severity="secondary" :label="$t('save')" @click="handleSave" />
      </div>
    </Form>
  </AdaptiveDialog>
</template>
